import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot } from "lucide-react";
import { 
  THINKING_STATUS_MESSAGES, 
  THINKING_CONFIG, 
  getStatusMessage 
} from "../constants/thinkingStatus";

const ThinkingIndicator = () => {
  const [thinkingTime, setThinkingTime] = useState(0);
  const [currentStatusIndex, setCurrentStatusIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Timer for thinking duration
  useEffect(() => {
    const interval = setInterval(() => {
      setThinkingTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Status message rotation
  useEffect(() => {
    const rotationInterval = setInterval(() => {
      setIsTransitioning(true);
      
      // Wait for fade out, then change message
      setTimeout(() => {
        setCurrentStatusIndex(prev => (prev + 1) % THINKING_STATUS_MESSAGES.length);
        setIsTransitioning(false);
      }, THINKING_CONFIG.FADE_DURATION / 2);
    }, THINKING_CONFIG.ROTATION_INTERVAL);

    return () => clearInterval(rotationInterval);
  }, []);

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -10 }}
      className="self-start bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-gray-200 rounded-2xl p-4 shadow-sm border-2 border-transparent transition-colors w-full max-w-full sm:w-96"
    >
      <div className="flex items-center gap-4">
        {/* Bot Icon */}
        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
          <motion.div
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <Bot className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </motion.div>
        </div>

        {/* Thinking Text and Timer */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="relative h-6 flex items-center mb-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStatusIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ 
                  opacity: isTransitioning ? 0 : 1, 
                  y: isTransitioning ? -10 : 0 
                }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  duration: THINKING_CONFIG.FADE_DURATION / 1000,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 flex items-center gap-2 whitespace-nowrap"
              >
                <span className="text-lg flex-shrink-0">
                  {getStatusMessage(currentStatusIndex).icon}
                </span>
                <span className="text-sm font-medium flex-shrink-0">
                  {getStatusMessage(currentStatusIndex).text}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatTime(thinkingTime)} • {getStatusMessage(currentStatusIndex).description}
          </div>
        </div>

        {/* Animated Dots */}
        <motion.div
          className="flex space-x-1.5 flex-shrink-0"
          animate={{
            opacity: [0.4, 1, 0.4],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-gray-500 dark:bg-gray-400 rounded-full"
              animate={{
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ThinkingIndicator;
